import Head from 'next/head'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import NextImage from 'next/image'
import {
	Box,
	Button,
	Text,
	Title,
	Container,
	Group,
	Card,
	List,
	ThemeIcon,
	Grid,
} from '@mantine/core'
import {
	IconArrowRight,
	IconCheck,
	IconMail,
	IconStar,
	IconUsers,
	IconMountain,
} from '@tabler/icons-react'

import * as Types from '../declarations'
import { withVersion } from 'lib/withVersion'
import { getDefaultImage } from 'lib/helper'

import PageTemplate from '../components/PageTemplate'

const About = ({ version }: { version: Types.VERSION }) => {
	const seo = {
		title: `For Guides - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description:
			version === Types.VERSION.TREKKING
				? 'Learn about Guided Trekking, a platform created to help hikers find guides, discover treks, and plan their next trekking adventures with ease.'
				: 'Learn about Guided Peaks, a platform created to help climbers find guides, discover mountains, and plan their next mountaineering adventures with ease.',
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		canonical: `https://www.guided${
			version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
		}.com/for-guides`,
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
	}

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<div id="guides-page">
					{version === Types.VERSION.PEAKS && (
						<>
							{/* Hero Section */}
							<Container size="xl" py="xl">
								<Box mb={50}>
									<Title order={1} size="3rem" mb="md">
										Grow Your Mountain Guiding Business
									</Title>
									<Text size="xl" color="dimmed" mb="xl" maw={600}>
										Connect directly with passionate mountaineers. No middlemen,
										no booking fees - just you and your clients building amazing
										mountain experiences together.
									</Text>
									<Group>
										<Link href="/guide">
											<Button
												size="lg"
												radius="md"
												rightSection={<IconArrowRight size={20} />}
											>
												Create Guide Profile
											</Button>
										</Link>
									</Group>
								</Box>

								{/* Key Benefits */}
								<Grid mb={50}>
									<Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
										<Card shadow="sm" p="lg" radius="md" withBorder h="100%">
											<ThemeIcon size={40} radius="md" mb="sm">
												<IconUsers size={24} />
											</ThemeIcon>
											<Text size="lg" fw={500} mb="xs">
												Direct Client Connection
											</Text>
											<Text size="sm" color="dimmed">
												Build direct relationships with clients without
												intermediaries cutting into your margins.
											</Text>
										</Card>
									</Grid.Col>
									<Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
										<Card shadow="sm" p="lg" radius="md" withBorder h="100%">
											<ThemeIcon size={40} radius="md" mb="sm">
												<IconMountain size={24} />
											</ThemeIcon>
											<Text size="lg" fw={500} mb="xs">
												Full Control
											</Text>
											<Text size="sm" color="dimmed">
												Set your own prices, schedules, and expedition details
												exactly how you want them.
											</Text>
										</Card>
									</Grid.Col>
									<Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
										<Card shadow="sm" p="lg" radius="md" withBorder h="100%">
											<ThemeIcon size={40} radius="md" mb="sm">
												<IconStar size={24} />
											</ThemeIcon>
											<Text size="lg" fw={500} mb="xs">
												Build Your Brand
											</Text>
											<Text size="sm" color="dimmed">
												Showcase your expertise and build your reputation in the
												mountaineering community.
											</Text>
										</Card>
									</Grid.Col>
								</Grid>

								{/* How It Works Section */}
								<Title order={2} mb="xl">
									How It Works
								</Title>
								<div className="how-it-works-section">
									<div className="how-it-works-row">
										<div className="image-container">
											<NextImage
												src={`https://www.guidedpeaks.com/cdn-cgi/image/width=1920,format=auto${'/static/homepage/guide-profile.png'}`}
												alt="Create guide profile"
												width={300}
												height={225}
											/>
										</div>
										<div className="content">
											<Title order={3}>Create Your Profile</Title>
											<List
												spacing="sm"
												size="lg"
												mt="md"
												icon={
													<ThemeIcon color="brand" size={24} radius="xl">
														<IconCheck size={16} />
													</ThemeIcon>
												}
											>
												<List.Item>
													Showcase your certifications and expertise
												</List.Item>
												<List.Item>
													Share your guiding experience and achievements
												</List.Item>
												<List.Item>
													Add photos and videos from your expeditions
												</List.Item>
											</List>
											<Link href="/guide">
												<Button
													size="md"
													radius="md"
													mt="lg"
													rightSection={<IconArrowRight size={20} />}
												>
													Guide dashboard
												</Button>
											</Link>
										</div>
									</div>
									<div className="how-it-works-row">
										<div className="image-container">
											<NextImage
												src={`https://www.guidedpeaks.com/cdn-cgi/image/width=1920,format=auto${'/static/homepage/guide-trips.png'}`}
												alt="List expeditions"
												width={300}
												height={225}
											/>
										</div>
										<div className="content">
											<Title order={3}>List Your Expeditions</Title>
											<List
												spacing="sm"
												size="lg"
												mt="md"
												icon={
													<ThemeIcon color="brand" size={24} radius="xl">
														<IconCheck size={16} />
													</ThemeIcon>
												}
											>
												<List.Item>
													Detail your unique expedition offerings
												</List.Item>
												<List.Item>Set group sizes and guide ratios</List.Item>
												<List.Item>
													Highlight special discounts and packages
												</List.Item>
											</List>
										</div>
									</div>
								</div>

								{/* Newsletter Promotion */}
								<Card
									withBorder
									shadow="sm"
									mt={80}
									mb={30}
									p="xl"
									radius="md"
									style={{
										borderColor: 'var(--mantine-color-brand-4)',
										borderWidth: 2,
									}}
								>
									<Group justify="apart" align="center">
										<div>
											<Title order={2} mb="xs">
												Want to reach more mountaineers?
											</Title>
											<Text size="lg" mb="xs">
												Featured guides reach thousands of mountaineers monthly
												through our newsletter and premium positions on the
												website.
											</Text>
											<Group gap="xs" mt="md">
												<Text size="md">Email</Text>
												<Text
													component="a"
													href="mailto:contact@guidedpeaks.com"
													fw={700}
													style={{ color: 'var(--mantine-color-brand-4)' }}
												>
													contact@guidedpeaks.com
												</Text>
												<Text size="md">to get featured</Text>
											</Group>
										</div>
										<Button
											component="a"
											href="mailto:contact@guidedpeaks.com"
											leftSection={<IconMail size={18} />}
											size="md"
											variant="outline"
										>
											Contact Us
										</Button>
									</Group>
								</Card>
							</Container>
						</>
					)}
				</div>
			</PageTemplate>
		</>
	)
}

export const getStaticProps = withVersion(
	async (context: { version?: Types.VERSION }) => {
		if (context?.version !== Types.VERSION.PEAKS) {
			return {
				redirect: {
					destination: '/404',
					permanent: false,
				},
			}
		}
		return { props: { version: context?.version } }
	},
)

export default About
